import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Lenis from "lenis";
import { motion } from "framer-motion";
import { Toaster } from "@/components/ui/sonner";
import { fetchContent } from "@/lib/api";
import Starfield from "@/components/Starfield";
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import Marquee from "@/components/Marquee";
import WhatIs from "@/components/WhatIs";
import Heroes from "@/components/Heroes";
import Worlds from "@/components/Worlds";
import Books from "@/components/Books";
import Timeline from "@/components/Timeline";
import Upcoming from "@/components/Upcoming";
import Trailer from "@/components/Trailer";
import About from "@/components/About";
import Contact from "@/components/Contact";
import Footer from "@/components/Footer";

const Home = ({ content }) => (
  <main className="relative">
    <Hero />
    <Marquee />
    <WhatIs />
    <Heroes characters={content.characters} />
    <Worlds worlds={content.worlds} />
    <Books books={content.books} />
    <Upcoming books={content.books} />
    <Timeline graph={content.graph} />
    <Trailer />
    <About />
    <Contact />
    <Footer />
  </main>
);

function App() {
  const [content, setContent] = useState(null);
  const [ready, setReady] = useState(false);
  const [slowLoad, setSlowLoad] = useState(false);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.25, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  useEffect(() => {
    const slow = setTimeout(() => setSlowLoad(true), 1500);
    fetchContent()
      .then((data) => setContent(data))
      .finally(() => {
        clearTimeout(slow);
        setReady(true);
      });
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: ready ? 1 : 0 }}
          transition={{ duration: 3, delay: 0.5, ease: "easeOut" }}
        >
          <Starfield />
          <div className="nebula nebula-a" />
          <div className="nebula nebula-b" />
        </motion.div>
        <motion.div
          data-testid="opening-veil"
          initial={{ opacity: 1 }}
          animate={{ opacity: ready ? 0 : 1 }}
          transition={{ duration: 2.4, delay: 0.3, ease: "easeInOut" }}
          style={{ pointerEvents: ready ? "none" : "auto" }}
          className="fixed inset-0 z-[95] flex items-center justify-center bg-[#030305]"
        >
          {slowLoad && !ready && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1 }}
              className="font-mono text-[10px] tracking-[0.5em] text-[#606070] uppercase"
            >
              Entering the universe
            </motion.p>
          )}
        </motion.div>
        {ready && content && (
          <>
            <Navbar />
            <Routes>
              <Route path="/" element={<Home content={content} />} />
            </Routes>
          </>
        )}
        <Toaster theme="dark" position="bottom-right" />
      </BrowserRouter>
    </div>
  );
}

export default App;
